import React from 'react';
import { ErrorPage } from './ErrorPage';
import * as Sentry from '@sentry/react';

interface Props {
    children: React.ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | undefined;
    errorInfo: React.ErrorInfo | undefined;
}

export class ErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: undefined, errorInfo: undefined };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error, errorInfo: undefined };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log to Sentry with additional context
        Sentry.withScope((scope) => {
            scope.setExtra('componentStack', errorInfo.componentStack);
            Sentry.captureException(error);
        });

        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.error('Error caught by boundary:', error);
            console.error('Component stack:', errorInfo.componentStack);
        }

        this.setState({
            error,
            errorInfo
        });
    }

    resetError = () => {
        this.setState({ 
            hasError: false, 
            error: undefined,
            errorInfo: undefined 
        });
    };

    render() {
        if (this.state.hasError) {
            return (
                <ErrorPage 
                    error={this.state.error} 
                    resetErrorBoundary={this.resetError}
                    errorInfo={this.state.errorInfo}
                />
            );
        }

        return this.props.children;
    }
} 